import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

import { v4 as uuid4 } from 'uuid';
import useFileUpload from './useFileUpload';

import { PDFThumbnailPlugin, JPEGRotationPlugin, HEIFToJPEGPlugin, makeFilenameFriendlyPlugin } from './plugins';
import { Box, LinearProgress, Typography, IconButton } from '@mui/material';
import ClearOutlined from '@mui/icons-material/ClearOutlined';

const DEFAULT_FILE_TYPES = [
    'css',
    'csv',
    'dng',
    'doc',
    'docx',
    'eml',
    'gif',
    'heic',
    'heif',
    'jpeg',
    'jpg',
    'json',
    'less',
    'mov',
    'mp3',
    'mp4',
    'msg',
    'numbers',
    'ods',
    'pages',
    'pdf',
    'ppt',
    'pptx',
    'png',
    'svg',
    'tiff',
    'txt',
    'wav',
    'xls',
    'xlsx',
    'zip',
];

export {
    DEFAULT_FILE_TYPES
};

export const ProgressBar = ({ percentage = 0, label = "", cancelButton, sx={}, ...rest }) => {
	return (
		<Box sx={{ display: "flex", alignItems: "center" }}>
			<Box sx={{ width: "100%", mr: 1, flex: '1 1 100%' }}>
				<LinearProgress variant="determinate" value={percentage} />
			</Box>
			<Box sx={{ flex: '0 0 auto', minWidth: 35, display: 'flex', gap: 1, alignItems: 'center' }}>
				<Typography variant="body2" color="text.secondary">
					{label && label + " "}
					{percentage}%
				</Typography>
				{cancelButton && cancelButton}
			</Box>
		</Box>
	);
};

const ErrorSegment = ({
    message,
    onClickRemove = () => {},
    time = 5000
}) => {

    useEffect(
        () => {
            const instance = setTimeout(
                () => {
                    onClickRemove();
                },
                time
            )
            return () => clearTimeout(instance);
        }
    );

    return <div>
        <div className='text-red-600'>{message}</div>
    </div>
}

const FileSegment = ({
    file,
    extra = {},
    workflow = 'v2',
    getUploadParams = async () => ({}),
    startUpload,
    onComplete = (resp) => {},
    onError = ({message}) => {},
    onClickRemove = () => {},
}) => {
    const [progress, setProgress] = useState(0);
    const {
        beginUpload,
        isUploading,
        abort
    } = useFileUpload({
        fileProp: file,
        extraData: extra,
        version: workflow,
        getUploadParams,
        onProgress: (e) => {
            setProgress( ((e.loaded / e.total) * 100).toFixed(2) )
        },
        onComplete: (resp) => {
            return onComplete(resp);
        },
        onError
    });

    useEffect(
        () => {
            return () => abort(); //on destruct - ABORT.
        },
        []
    )

    useEffect(
        () => {
            if(startUpload && !isUploading) {
                beginUpload();
            }
        },
        [startUpload]
    );

    return <Box sx={{display: 'flex', flexDirection: 'column'}}>
    {file.name && <label className='m-0 p-0'>{file.name}</label>}
    <ProgressBar cancelButton={<IconButton onClick={onClickRemove}><ClearOutlined /></IconButton>} percentage={progress} />
</Box>
}


//refactor using - useImperativeHandle

const FileUpload = forwardRef(({
    dropText = "click or drop here",
    accepts = DEFAULT_FILE_TYPES,
    maxSize = 1024 * 1024 * 80,
    plugins = [makeFilenameFriendlyPlugin, PDFThumbnailPlugin, HEIFToJPEGPlugin, JPEGRotationPlugin], //add default middleware here.
    maxFiles = 0,
    workflow = 'v2',
    autoUpload = false,
    onComplete = (resp) => {},
    onError = ({message}) => {},
    uploadParams = async () => ({}),
    displayErrors = true
}, ref) => {

    const [privateErrors, setPrivateErrors] = useState([]);
    
    useImperativeHandle(
        ref, () => {
            return {
                startUpload: () => {
                    if(Object.keys(fileQueue) == 0) {
                        onComplete([]);
                    } else {
                        setStartedUpload(true);
                    }
                },
                reset: () => {
                    setStartedUpload(false);
                    setFileQueue({});
                },
                abort: () => {
                    setStartedUpload(false);
                }
            }
        }
    );

    const fileRef = useRef(ref);
    const [isDragOver, setDragOver] = useState(false);
    const [fileQueue, setFileQueue] = useState({});
    const [startedUpload, setStartedUpload] = useState(autoUpload);

    const processComplete = () => {
        if(Object.values(fileQueue).findIndex(
            (entry) => entry.state != 'complete'
        ) != -1) return;

        onComplete(Object.values(fileQueue));
        //we can clear it out now.
        if(!autoUpload) setStartedUpload(false);


       
        setFileQueue((queue) => {
            let newEntry = {};
            Object.keys(queue).forEach(
                (key) => {
                    if(queue[key].state == 'complete') return;
                    newEntry[key] = queue[key];
                }
            );
            return newEntry;
        });

    }

    const handleClick = () => fileRef.current.click();
    const addNewFiles = async (newFiles) => {

        for(let nfKey in newFiles) {


            for(let plugin of plugins) {
                let result = await plugin(newFiles[nfKey].file).catch(() => false);
                if(result === false) continue;
                newFiles[nfKey] = {
                    ...newFiles[nfKey],
                    ...result,
                    ...{
                        extra: {
                            ...(newFiles[nfKey].extra ?? {}),
                            ...(result.extra ?? {})
                        }
                    }
                }
            }
        }
        let newFileQueue = {
            ...newFiles,
            ...fileQueue
        };
        if(maxFiles > 0) {

            const newItemKeys = Object.keys(newFileQueue);

            if(newItemKeys.length > maxFiles) {
                //on the hunt for a wabbit.
                newFileQueue = newItemKeys.filter(
                    (k, index) => index < maxFiles
                ).reduce(
                    (aggregate, val) => {
                        aggregate[val] = newFileQueue[val];
                        return aggregate;
                    },
                    {}
                );
            }
        }

        setFileQueue(newFileQueue);
    }
    
    const handleFileDrop = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        setDragOver(false);

        const errors = [];

        const files = ev.dataTransfer.items ?
            [...ev.dataTransfer.items].filter( (i) => i.kind === 'file').map ( i => i.getAsFile() ) :
            [...ev.dataTransfer.files];

        const newFiles = files.reduce(
            (aggregate, value) => {
                if(fileIsAccepted(value)) {
                    aggregate[uuid4()] = {
                        file: value,
                        state: 'pending'
                    }
                } else {
                    errors.push({
                        id: uuid4(),
                        msg: `File ${value.name} is not an accepted file type.`
                    });
                }
                return aggregate;
            },
            {}
        );

        if(errors.length > 0) {
            onError({message: errors.map((e) => e.msg).join("\n")});
            setPrivateErrors(
                (
                    prev
                ) => {
                    return [
                        ...prev,
                        ...errors
                    ]
                }
            )
        }

        addNewFiles(newFiles);
    }
    
    const fileIsAccepted = (file) => {
        if(accepts.includes("*")) return true;

        const ext = file.name.split('.').pop();

        return accepts.includes( (ext+'').toLowerCase() );
    }

    const onFileChange = (ev) => {
        if(ev.target.value == null) return;

        const errors = [];

        addNewFiles(
            Object.values(ev.target.files).reduce(
                (aggregate, value) => {
                    if(fileIsAccepted(value)) {
                        aggregate[uuid4()] = {
                            file: value,
                            state: 'pending'
                        };
                    } else {
                        errors.push({
                            id: uuid4(),
                            msg: `File ${value.name} is not an accepted file type.`
                        });
                    }
                    return aggregate;
                },
                {}
            )
        );

        if(errors.length > 0) {
            onError({message: errors.map(e => e.msg).join("\n")});
            setPrivateErrors(
                (
                    prev
                ) => {
                    return [
                        ...prev,
                        ...errors
                    ]
                }
            )
        }
    }

    const handleDragOver = (ev) => {
        setDragOver(true);
        ev.preventDefault();
    }

    return <div className='w-full'>
        <input ref={fileRef} type="file" onChange={onFileChange} style={{display: 'none'}} multiple={maxFiles > 1 || maxFiles == 0 ? true : false} />
        <div style={{display: "block"}} onClick={handleClick} className="bg-form-secondary" onDrop={handleFileDrop} onDragOver={handleDragOver} onDragLeave={() => setDragOver(false)}>
            <div className={`grid grid-cols-1 gap-1 place-items-center h-full min-h-[100px] border-1 border-formitizeblue ${isDragOver ? 'border-dotted' : ''}`}>
                <div>{dropText}</div>
            </div>
        </div>
        <div className='m-t-5'>
            {Object.keys(fileQueue).map(
                (key) => <FileSegment 
                    getUploadParams={uploadParams} 
                    workflow={workflow} 
                    startUpload={startedUpload} 
                    accepts 
                    key={key} 
                    file={fileQueue[key].file} 
                    extra={fileQueue[key].extra}
                    onError={onError}
                    onComplete={(resp) => {
                        fileQueue[key].state = 'complete'; //it's a mutation. But the UI isn't relying on the individual state of it.
                        fileQueue[key].stateResp = resp;
                        processComplete();
                    }}
                    onClickRemove={() => {
                        const { [key]: toRemove, ...entries} = fileQueue;
                        setFileQueue(() => ({
                            ...entries
                        }));
                    }} 
                />
            )}
        </div>
        {displayErrors && 
            <>
                {privateErrors.map(
                    (err) => <ErrorSegment 
                        key={err.id} 
                        message={err.msg} 
                        onClickRemove={() => {
                            setPrivateErrors(
                                (
                                    prev
                                ) => {
                                    return prev.filter(
                                        (e) => e.id != err.id
                                    )
                                }
                            )
                        }}
                    />
                )}
            </>
        }
    </div>

});

export default FileUpload;